/**
 * ResStatusPill — Tier 0 slice-local primitive (epic SCRUM-332 / SCRUM-333).
 *
 * Maps a reservation FSM status onto a kit `<Badge>` variant + a human
 * label, with an optional "modified after creation" pencil indicator.
 * Rendered by the (future) reservations grid rows, the detail hero, and
 * the dashboard's upcoming-arrivals card.
 *
 * **Design source.** `design_handoff_reservations/` (the reservations
 * bundle) — VISUAL reference only. The bundle's `Reservation` model +
 * `RES_TYPES` are invented; per SCRUM-333 the renders win but the data
 * model is STALE, so this primitive keys off the REAL wire enum
 * `RESERVATION_STATUS_WIRE_VALUES` from `@artemis/validators`
 * (`QUO|HOLD|BKD|CI|CO|CXL|HIST`) — never a redefined local enum
 * (CLAUDE.md FE rule 4: validators are the single source of truth).
 *
 * **Tokens (ADR frontend-0003).** No color/hex here at all — the `<Badge>`
 * variant carries every tint through the kit's Tier-1 branded scales.
 * Status is conveyed by the TEXT label (never color alone — WCAG 2.1 AA,
 * FE rule 7).
 *
 * **Tier (ADR frontend-0004).** Tier 0 today: slice-local, NOT
 * barrel-exported. The reactive second-consumer trigger fires — promote
 * to `widgets/` — when a second slice (e.g. the cross-BC dashboard)
 * adopts the same status→variant map rather than duplicating it.
 */

import * as React from 'react';
import { RESERVATION_STATUS_WIRE_VALUES } from '@artemis/validators';
import { Badge, type BadgeVariant, Icon } from '@sparta/ui-kit';

/**
 * The closed set of wire status values, re-derived from the validators
 * const so the union can never drift from the source of truth. This is a
 * TYPE alias only — the runtime list stays owned by `@artemis/validators`.
 */
export type ReservationStatusWire = (typeof RESERVATION_STATUS_WIRE_VALUES)[number];

interface StatusPresentation {
  /** Kit Badge variant — chosen from the exact `BadgeVariant` union. */
  readonly variant: BadgeVariant;
  /** Human-readable label. The label carries the state (never color alone). */
  readonly label: string;
}

/**
 * status → (variant, label). Keyed off the imported wire values so a new
 * status added to the validators enum surfaces as a TypeScript error here
 * (the `Record<ReservationStatusWire, …>` is exhaustive).
 *
 * Variant intent (from SCRUM-333): Booked=success, Checked-in=brand,
 * Checked-out/Historical=neutral, Quote/Hold=warning, Cancelled=danger.
 * SCRUM-362: Overbooked=danger — a quarantined channel-ingest loser that
 * needs operator resolution (winner-select / relocate / cancel), so it reads
 * as a problem state alongside Cancelled.
 */
const STATUS_PRESENTATION: Readonly<Record<ReservationStatusWire, StatusPresentation>> = {
  BKD: { variant: 'success', label: 'Booked' },
  CI: { variant: 'brand', label: 'Checked-in' },
  CO: { variant: 'neutral', label: 'Checked-out' },
  QUO: { variant: 'warning', label: 'Quote' },
  CXL: { variant: 'danger', label: 'Cancelled' },
  HOLD: { variant: 'warning', label: 'Hold' },
  HIST: { variant: 'neutral', label: 'Historical' },
  overbooked: { variant: 'danger', label: 'Overbooked' },
};

/**
 * Fallback for an out-of-contract status string (defensive — the wire
 * type is closed, but a DTO drift or a hand-built fixture could feed an
 * unknown value; we render the raw string neutrally rather than crash or
 * paint a misleading verdict).
 */
function presentationFor(status: string): StatusPresentation {
  if (Object.prototype.hasOwnProperty.call(STATUS_PRESENTATION, status)) {
    return STATUS_PRESENTATION[status as ReservationStatusWire];
  }
  // Unknown/empty/malformed → neutral, echo the raw string (React escapes
  // it, so an injection-shaped value is inert) or a generic marker when
  // there is nothing to show.
  return { variant: 'neutral', label: status.length > 0 ? status : 'Unknown' };
}

/**
 * The human display label for a wire status — the SINGLE SOURCE OF TRUTH for
 * status copy, shared by the pill itself and by any surface that must render
 * the same label OUTSIDE a pill (e.g. the ReservationsGrid status-filter
 * options). Reuses `STATUS_PRESENTATION` (and its defensive fallback) so the
 * filter's option text can never drift from the pill's badge text. Behaviour
 * matches the pill exactly: known wire values return their canonical label,
 * unknown/malformed values echo the raw string, and empty returns "Unknown".
 */
export function statusLabelFor(status: string): string {
  return presentationFor(status).label;
}

export interface ResStatusPillProps {
  /** Wire status. Typed `string` because the DTO types it `string`; the
   *  map is exhaustive over the closed set and falls back for anything else. */
  readonly status: string;
  /** When true, append a pencil glyph indicating the reservation was
   *  modified after creation. */
  readonly modified?: boolean;
}

export function ResStatusPill({
  status,
  modified = false,
}: ResStatusPillProps): React.ReactElement {
  const { variant, label } = presentationFor(status);

  return (
    <span className="inline-flex items-center gap-1">
      {/* size="sm" matches the design's compact pill (reservations.jsx:36) —
          the smaller token set sits correctly in dense grid rows + the hero. */}
      <Badge variant={variant} size="sm">
        {label}
      </Badge>
      {modified ? (
        // `pencil` is a registered `IconName` (ui-kit Icon REGISTRY).
        // `title` gives the hover/AT affordance; the glyph is decorative
        // (`aria-hidden` on the wrapping span) so it isn't double-announced,
        // and the meaning is carried by the title + the visible pill label.
        // Warning-amber tint (reservations.jsx:37) makes "edited after creation"
        // an at-a-glance signal, not a muted afterthought.
        <span
          aria-hidden="true"
          title="Modified after creation"
          className="inline-flex items-center text-[color:var(--warning-500)]"
        >
          <Icon name="pencil" size={12} />
        </span>
      ) : null}
    </span>
  );
}
